import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, TrendingDown, Calendar, Users, CreditCard, BarChart3 } from 'lucide-react';
import Card from '../../components/UI/Card';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import financeService from '../../services/financeService';
import toast from 'react-hot-toast';

const FinanceContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 10px 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: #b8b8d1;
  font-size: 16px;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 25px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient || 'linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)'};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.gradient || 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
`;

const StatValue = styled.div`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #b8b8d1;
  font-size: 14px;
`;

const StatChange = styled.div`
  color: ${props => props.positive ? '#4ecdc4' : '#ff6b6b'};
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled(Card)`
  min-height: 300px;
`;

const ChartTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChartPlaceholder = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c6c8a;
  font-size: 16px;
`;

const RecentTransactions = styled.div`
  margin-top: 20px;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const TransactionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TransactionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const TransactionDetails = styled.div`
  flex: 1;
`;

const TransactionTitle = styled.div`
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 5px;
`;

const TransactionDate = styled.div`
  color: #6c6c8a;
  font-size: 12px;
`;

const TransactionAmount = styled.div`
  color: ${props => props.positive ? '#4ecdc4' : '#ff6b6b'};
  font-weight: 600;
  font-size: 16px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const Finance = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);
      const data = await financeService.getSummary();
      setSummary(data);
    } catch (error) {
      toast.error('خطا در بارگذاری اطلاعات مالی');
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fa-IR', {
      style: 'currency',
      currency: 'IRR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  if (loading) {
    return (
      <FinanceContainer>
        <LoadingSpinner>در حال بارگذاری اطلاعات مالی...</LoadingSpinner>
      </FinanceContainer>
    );
  }

  return (
    <FinanceContainer>
      <Header>
        <Title>داشبورد مالی</Title>
        <Subtitle>آمار و گزارشات مالی سیستم</Subtitle>
      </Header>

      <StatsGrid>
        <StatCard
          gradient="linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)">
              <DollarSign size={24} color="white" />
            </StatIcon>
            <StatChange positive>
              <TrendingUp size={16} />
              +12%
            </StatChange>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.totalEarnings)}</StatValue>
          <StatLabel>کل درآمد</StatLabel>
        </StatCard>

        <StatCard
          gradient="linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)">
              <Calendar size={24} color="white" />
            </StatIcon>
            <StatChange positive>
              <TrendingUp size={16} />
              +8%
            </StatChange>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.earningsThisMonth)}</StatValue>
          <StatLabel>درآمد این ماه</StatLabel>
        </StatCard>

        <StatCard
          gradient="linear-gradient(90deg, #ff6b6b 0%, #ff8e8e 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)">
              <CreditCard size={24} color="white" />
            </StatIcon>
            <StatChange positive>
              <TrendingUp size={16} />
              +5%
            </StatChange>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.earningsToday)}</StatValue>
          <StatLabel>درآمد امروز</StatLabel>
        </StatCard>

        <StatCard
          gradient="linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
              <Users size={24} color="white" />
            </StatIcon>
            <StatChange positive>
              <TrendingUp size={16} />
              +15%
            </StatChange>
          </StatHeader>
          <StatValue>{summary?.totalTransactions || 0}</StatValue>
          <StatLabel>کل تراکنش‌ها</StatLabel>
        </StatCard>
      </StatsGrid>

      <ChartSection>
        <ChartCard>
          <ChartTitle>
            <BarChart3 size={20} />
            نمودار درآمد
          </ChartTitle>
          <ChartPlaceholder>
            نمودار درآمد در حال توسعه است
          </ChartPlaceholder>
        </ChartCard>

        <Card>
          <ChartTitle>
            <TrendingUp size={20} />
            آمار سریع
          </ChartTitle>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', color: '#00d4ff', fontWeight: 'bold', marginBottom: '10px' }}>
              98%
            </div>
            <div style={{ color: '#b8b8d1', fontSize: '16px', marginBottom: '20px' }}>
              رضایت کاربران
            </div>
            <div style={{ fontSize: '14px', color: '#6c6c8a' }}>
              عملکرد سیستم در بالاترین سطح
            </div>
          </div>
        </Card>
      </ChartSection>

      {summary?.recentTransactions && summary.recentTransactions.length > 0 && (
        <Card>
          <ChartTitle>
            <CreditCard size={20} />
            تراکنش‌های اخیر
          </ChartTitle>
          <RecentTransactions>
            {summary.recentTransactions.slice(0, 5).map((transaction, index) => (
              <TransactionItem key={index}>
                <TransactionInfo>
                  <TransactionIcon>
                    <CreditCard size={20} />
                  </TransactionIcon>
                  <TransactionDetails>
                    <TransactionTitle>{transaction.description || 'تراکنش'}</TransactionTitle>
                    <TransactionDate>{formatDate(transaction.date)}</TransactionDate>
                  </TransactionDetails>
                </TransactionInfo>
                <TransactionAmount positive={transaction.amount > 0}>
                  {formatCurrency(transaction.amount)}
                </TransactionAmount>
              </TransactionItem>
            ))}
          </RecentTransactions>
        </Card>
      )}
    </FinanceContainer>
  );
};

export default Finance;
